/**
 * Tradução de tipo de instância para vCPU e memória.
 *
 * Porte fiel da tabela `instanceSpecs` de `construtorps.html`. Serve para enriquecer a descrição
 * do item na proposta: em vez de exibir `m6i.2xlarge`, exibe `8 vCPU, 32 GiB RAM`, que é o que o
 * cliente entende.
 *
 * Quando o tipo não está na tabela, o parser mantém o nome original — comportamento do legado.
 * Acrescentar famílias novas aqui não quebra nada.
 */

export const ESPECIFICACOES_INSTANCIA: Readonly<Record<string, string>> = {
  // --- EC2 uso geral, AMD ---
  't3a.nano': '2 vCPU, 0.5 GiB RAM',
  't3a.micro': '2 vCPU, 1 GiB RAM',
  't3a.small': '2 vCPU, 2 GiB RAM',
  't3a.medium': '2 vCPU, 4 GiB RAM',
  't3a.large': '2 vCPU, 8 GiB RAM',
  't3a.xlarge': '4 vCPU, 16 GiB RAM',
  't3a.2xlarge': '8 vCPU, 32 GiB RAM',
  'm6a.large': '2 vCPU, 8 GiB RAM',
  'm6a.xlarge': '4 vCPU, 16 GiB RAM',
  'm6a.2xlarge': '8 vCPU, 32 GiB RAM',
  'm6a.4xlarge': '16 vCPU, 64 GiB RAM',
  'm6a.8xlarge': '32 vCPU, 128 GiB RAM',
  'm6a.12xlarge': '48 vCPU, 192 GiB RAM',
  'm6a.16xlarge': '64 vCPU, 256 GiB RAM',
  'm6a.24xlarge': '96 vCPU, 384 GiB RAM',
  'm6a.32xlarge': '128 vCPU, 512 GiB RAM',
  'm6a.48xlarge': '192 vCPU, 768 GiB RAM',

  // --- EC2 uso geral, Graviton ---
  't4g.nano': '2 vCPU, 0.5 GiB RAM',
  't4g.micro': '2 vCPU, 1 GiB RAM',
  't4g.small': '2 vCPU, 2 GiB RAM',
  't4g.medium': '2 vCPU, 4 GiB RAM',
  't4g.large': '2 vCPU, 8 GiB RAM',
  't4g.xlarge': '4 vCPU, 16 GiB RAM',
  't4g.2xlarge': '8 vCPU, 32 GiB RAM',
  'm6g.medium': '1 vCPU, 4 GiB RAM',
  'm6g.large': '2 vCPU, 8 GiB RAM',
  'm6g.xlarge': '4 vCPU, 16 GiB RAM',
  'm6g.2xlarge': '8 vCPU, 32 GiB RAM',
  'm6g.4xlarge': '16 vCPU, 64 GiB RAM',
  'm6g.8xlarge': '32 vCPU, 128 GiB RAM',
  'm6g.12xlarge': '48 vCPU, 192 GiB RAM',
  'm6g.16xlarge': '64 vCPU, 256 GiB RAM',
  'm6g.metal': '64 vCPU, 256 GiB RAM',
  'm7g.large': '2 vCPU, 8 GiB RAM',
  'm7g.xlarge': '4 vCPU, 16 GiB RAM',
  'm7g.2xlarge': '8 vCPU, 32 GiB RAM',
  'm7g.4xlarge': '16 vCPU, 64 GiB RAM',
  'm7g.8xlarge': '32 vCPU, 128 GiB RAM',
  'm7g.12xlarge': '48 vCPU, 192 GiB RAM',
  'm7g.16xlarge': '64 vCPU, 256 GiB RAM',

  // --- EC2 uso geral, Intel ---
  'm6i.large': '2 vCPU, 8 GiB RAM',
  'm6i.xlarge': '4 vCPU, 16 GiB RAM',
  'm6i.2xlarge': '8 vCPU, 32 GiB RAM',
  'm6i.4xlarge': '16 vCPU, 64 GiB RAM',
  'm6i.8xlarge': '32 vCPU, 128 GiB RAM',
  'm6i.12xlarge': '48 vCPU, 192 GiB RAM',
  'm6i.16xlarge': '64 vCPU, 256 GiB RAM',
  'm6i.24xlarge': '96 vCPU, 384 GiB RAM',
  'm6i.32xlarge': '128 vCPU, 512 GiB RAM',
  'm5.large': '2 vCPU, 8 GiB RAM',
  'm5.xlarge': '4 vCPU, 16 GiB RAM',
  'm5.2xlarge': '8 vCPU, 32 GiB RAM',
  'm5.4xlarge': '16 vCPU, 64 GiB RAM',
  'm5.8xlarge': '32 vCPU, 128 GiB RAM',
  'm5.12xlarge': '48 vCPU, 192 GiB RAM',
  'm5.16xlarge': '64 vCPU, 256 GiB RAM',
  'm5.24xlarge': '96 vCPU, 384 GiB RAM',
  't3.nano': '2 vCPU, 0.5 GiB RAM',
  't3.micro': '2 vCPU, 1 GiB RAM',
  't3.small': '2 vCPU, 2 GiB RAM',
  't3.medium': '2 vCPU, 4 GiB RAM',
  't3.large': '2 vCPU, 8 GiB RAM',
  't3.xlarge': '4 vCPU, 16 GiB RAM',
  't3.2xlarge': '8 vCPU, 32 GiB RAM',

  // --- EC2 otimizado para computação ---
  'c6g.large': '2 vCPU, 4 GiB RAM',
  'c6g.xlarge': '4 vCPU, 8 GiB RAM',
  'c6g.2xlarge': '8 vCPU, 16 GiB RAM',
  'c6g.4xlarge': '16 vCPU, 32 GiB RAM',
  'c6g.8xlarge': '32 vCPU, 64 GiB RAM',
  'c6g.12xlarge': '48 vCPU, 96 GiB RAM',
  'c6g.16xlarge': '64 vCPU, 128 GiB RAM',
  'c7g.medium': '1 vCPU, 2 GiB RAM',
  'c7g.large': '2 vCPU, 4 GiB RAM',
  'c7g.xlarge': '4 vCPU, 8 GiB RAM',
  'c7g.2xlarge': '8 vCPU, 16 GiB RAM',
  'c7g.4xlarge': '16 vCPU, 32 GiB RAM',
  'c7g.8xlarge': '32 vCPU, 64 GiB RAM',
  'c7g.12xlarge': '48 vCPU, 96 GiB RAM',
  'c7g.16xlarge': '64 vCPU, 128 GiB RAM',
  'c6i.large': '2 vCPU, 4 GiB RAM',
  'c6i.xlarge': '4 vCPU, 8 GiB RAM',
  'c6i.2xlarge': '8 vCPU, 16 GiB RAM',
  'c6i.4xlarge': '16 vCPU, 32 GiB RAM',
  'c6i.8xlarge': '32 vCPU, 64 GiB RAM',
  'c6i.12xlarge': '48 vCPU, 96 GiB RAM',
  'c6i.16xlarge': '64 vCPU, 128 GiB RAM',
  'c6i.24xlarge': '96 vCPU, 192 GiB RAM',
  'c6i.32xlarge': '128 vCPU, 256 GiB RAM',
  'c6i.metal': '128 vCPU, 256 GiB RAM',
  'c5.large': '2 vCPU, 4 GiB RAM',
  'c5.xlarge': '4 vCPU, 8 GiB RAM',
  'c5.2xlarge': '8 vCPU, 16 GiB RAM',
  'c5.4xlarge': '16 vCPU, 32 GiB RAM',
  'c5.9xlarge': '36 vCPU, 72 GiB RAM',
  'c5.12xlarge': '48 vCPU, 96 GiB RAM',
  'c5.18xlarge': '72 vCPU, 144 GiB RAM',
  'c5.24xlarge': '96 vCPU, 192 GiB RAM',
  'c5.metal': '96 vCPU, 192 GiB RAM',
  'c5a.large': '2 vCPU, 4 GiB RAM',
  'c5a.xlarge': '4 vCPU, 8 GiB RAM',
  'c5a.2xlarge': '8 vCPU, 16 GiB RAM',
  'c5a.4xlarge': '16 vCPU, 32 GiB RAM',
  'c5a.8xlarge': '32 vCPU, 64 GiB RAM',
  'c5a.12xlarge': '48 vCPU, 96 GiB RAM',
  'c5a.16xlarge': '64 vCPU, 128 GiB RAM',
  'c5a.24xlarge': '96 vCPU, 192 GiB RAM',
  'c6a.large': '2 vCPU, 4 GiB RAM',
  'c6a.xlarge': '4 vCPU, 8 GiB RAM',
  'c6a.2xlarge': '8 vCPU, 16 GiB RAM',
  'c6a.4xlarge': '16 vCPU, 32 GiB RAM',
  'c6a.8xlarge': '32 vCPU, 64 GiB RAM',
  'c6a.12xlarge': '48 vCPU, 96 GiB RAM',
  'c6a.16xlarge': '64 vCPU, 128 GiB RAM',
  'c6a.24xlarge': '96 vCPU, 192 GiB RAM',
  'c6a.32xlarge': '128 vCPU, 256 GiB RAM',
  'c6a.48xlarge': '192 vCPU, 384 GiB RAM',
  'c6a.metal': '192 vCPU, 384 GiB RAM',

  // --- EC2 otimizado para memória ---
  'r4.large': '2 vCPU, 15.25 GiB RAM',
  'r4.xlarge': '4 vCPU, 30.5 GiB RAM',
  'r4.2xlarge': '8 vCPU, 61 GiB RAM',
  'r4.4xlarge': '16 vCPU, 122 GiB RAM',
  'r4.8xlarge': '32 vCPU, 244 GiB RAM',
  'r4.16xlarge': '64 vCPU, 488 GiB RAM',
  'r5.large': '2 vCPU, 16 GiB RAM',
  'r5.xlarge': '4 vCPU, 32 GiB RAM',
  'r5.2xlarge': '8 vCPU, 64 GiB RAM',
  'r5.4xlarge': '16 vCPU, 128 GiB RAM',
  'r5.8xlarge': '32 vCPU, 256 GiB RAM',
  'r5.12xlarge': '48 vCPU, 384 GiB RAM',
  'r5.16xlarge': '64 vCPU, 512 GiB RAM',
  'r5.24xlarge': '96 vCPU, 768 GiB RAM',
  'r5a.large': '2 vCPU, 16 GiB RAM',
  'r5a.xlarge': '4 vCPU, 32 GiB RAM',
  'r5a.2xlarge': '8 vCPU, 64 GiB RAM',
  'r5a.4xlarge': '16 vCPU, 128 GiB RAM',
  'r5a.8xlarge': '32 vCPU, 256 GiB RAM',
  'r5a.12xlarge': '48 vCPU, 384 GiB RAM',
  'r5a.16xlarge': '64 vCPU, 512 GiB RAM',
  'r5a.24xlarge': '96 vCPU, 768 GiB RAM',
  'r6g.large': '2 vCPU, 16 GiB RAM',
  'r6g.xlarge': '4 vCPU, 32 GiB RAM',
  'r6g.2xlarge': '8 vCPU, 64 GiB RAM',
  'r6g.4xlarge': '16 vCPU, 128 GiB RAM',
  'r6g.8xlarge': '32 vCPU, 256 GiB RAM',
  'r6g.12xlarge': '48 vCPU, 384 GiB RAM',
  'r6g.16xlarge': '64 vCPU, 512 GiB RAM',
  'r6i.large': '2 vCPU, 16 GiB RAM',
  'r6i.xlarge': '4 vCPU, 32 GiB RAM',
  'r6i.2xlarge': '8 vCPU, 64 GiB RAM',
  'r6i.4xlarge': '16 vCPU, 128 GiB RAM',
  'r6i.8xlarge': '32 vCPU, 256 GiB RAM',
  'r6i.12xlarge': '48 vCPU, 384 GiB RAM',
  'r6i.16xlarge': '64 vCPU, 512 GiB RAM',
  'r7g.large': '2 vCPU, 16 GiB RAM',
  'r7g.xlarge': '4 vCPU, 32 GiB RAM',
  'r7g.2xlarge': '8 vCPU, 64 GiB RAM',
  'r7g.4xlarge': '16 vCPU, 128 GiB RAM',
  'r7g.8xlarge': '32 vCPU, 256 GiB RAM',
  'r7g.12xlarge': '48 vCPU, 384 GiB RAM',
  'r7g.16xlarge': '64 vCPU, 512 GiB RAM',
  'r6a.large': '2 vCPU, 16 GiB RAM',
  'r6a.xlarge': '4 vCPU, 32 GiB RAM',
  'r6a.2xlarge': '8 vCPU, 64 GiB RAM',
  'r6a.4xlarge': '16 vCPU, 128 GiB RAM',
  'r6a.8xlarge': '32 vCPU, 256 GiB RAM',
  'r6a.12xlarge': '48 vCPU, 384 GiB RAM',
  'r6a.16xlarge': '64 vCPU, 512 GiB RAM',

  // --- RDS, Graviton ---
  'db.t4g.micro': '2 vCPU, 1 GiB RAM',
  'db.t4g.small': '2 vCPU, 2 GiB RAM',
  'db.t4g.medium': '2 vCPU, 4 GiB RAM',
  'db.t4g.large': '2 vCPU, 8 GiB RAM',
  'db.t4g.xlarge': '4 vCPU, 16 GiB RAM',
  'db.t4g.2xlarge': '8 vCPU, 32 GiB RAM',
  'db.m6g.large': '2 vCPU, 8 GiB RAM',
  'db.m6g.xlarge': '4 vCPU, 16 GiB RAM',
  'db.m6g.2xlarge': '8 vCPU, 32 GiB RAM',
  'db.m6g.4xlarge': '16 vCPU, 64 GiB RAM',
  'db.m6g.8xlarge': '32 vCPU, 128 GiB RAM',
  'db.m6g.12xlarge': '48 vCPU, 192 GiB RAM',
  'db.m6g.16xlarge': '64 vCPU, 256 GiB RAM',
  'db.r6g.large': '2 vCPU, 16 GiB RAM',
  'db.r6g.xlarge': '4 vCPU, 32 GiB RAM',
  'db.r6g.2xlarge': '8 vCPU, 64 GiB RAM',
  'db.r6g.4xlarge': '16 vCPU, 128 GiB RAM',
  'db.r6g.8xlarge': '32 vCPU, 256 GiB RAM',
  'db.r6g.12xlarge': '48 vCPU, 384 GiB RAM',
  'db.r6g.16xlarge': '64 vCPU, 512 GiB RAM',
  'db.r7g.large': '2 vCPU, 16 GiB RAM',
  'db.r7g.xlarge': '4 vCPU, 32 GiB RAM',
  'db.r7g.2xlarge': '8 vCPU, 64 GiB RAM',
  'db.r7g.4xlarge': '16 vCPU, 128 GiB RAM',
  'db.r7g.8xlarge': '32 vCPU, 256 GiB RAM',
  'db.r7g.12xlarge': '48 vCPU, 384 GiB RAM',
  'db.r7g.16xlarge': '64 vCPU, 512 GiB RAM',

  // --- RDS, Intel e AMD ---
  'db.t3.micro': '2 vCPU, 1 GiB RAM',
  'db.t3.small': '2 vCPU, 2 GiB RAM',
  'db.t3.medium': '2 vCPU, 4 GiB RAM',
  'db.t3.large': '2 vCPU, 8 GiB RAM',
  'db.t3.xlarge': '4 vCPU, 16 GiB RAM',
  'db.t3.2xlarge': '8 vCPU, 32 GiB RAM',
  'db.m5.large': '2 vCPU, 8 GiB RAM',
  'db.m5.xlarge': '4 vCPU, 16 GiB RAM',
  'db.m5.2xlarge': '8 vCPU, 32 GiB RAM',
  'db.m5.4xlarge': '16 vCPU, 64 GiB RAM',
  'db.m5.8xlarge': '32 vCPU, 128 GiB RAM',
  'db.m5.12xlarge': '48 vCPU, 192 GiB RAM',
  'db.m5.16xlarge': '64 vCPU, 256 GiB RAM',
  'db.m5.24xlarge': '96 vCPU, 384 GiB RAM',
  'db.m6i.large': '2 vCPU, 8 GiB RAM',
  'db.m6i.xlarge': '4 vCPU, 16 GiB RAM',
  'db.m6i.2xlarge': '8 vCPU, 32 GiB RAM',
  'db.m6i.4xlarge': '16 vCPU, 64 GiB RAM',
  'db.m6i.8xlarge': '32 vCPU, 128 GiB RAM',
  'db.m6i.12xlarge': '48 vCPU, 192 GiB RAM',
  'db.m6i.16xlarge': '64 vCPU, 256 GiB RAM',
  'db.m6i.24xlarge': '96 vCPU, 384 GiB RAM',
  'db.m6i.32xlarge': '128 vCPU, 512 GiB RAM',
  'db.r5.large': '2 vCPU, 16 GiB RAM',
  'db.r5.xlarge': '4 vCPU, 32 GiB RAM',
  'db.r5.2xlarge': '8 vCPU, 64 GiB RAM',
  'db.r5.4xlarge': '16 vCPU, 128 GiB RAM',
  'db.r5.8xlarge': '32 vCPU, 256 GiB RAM',
  'db.r5.12xlarge': '48 vCPU, 384 GiB RAM',
  'db.r5.16xlarge': '64 vCPU, 512 GiB RAM',
  'db.r5.24xlarge': '96 vCPU, 768 GiB RAM',
  'db.r6i.large': '2 vCPU, 16 GiB RAM',
  'db.r6i.xlarge': '4 vCPU, 32 GiB RAM',
  'db.r6i.2xlarge': '8 vCPU, 64 GiB RAM',
  'db.r6i.4xlarge': '16 vCPU, 128 GiB RAM',
  'db.r6i.8xlarge': '32 vCPU, 256 GiB RAM',
  'db.r6i.12xlarge': '48 vCPU, 384 GiB RAM',
  'db.r6i.16xlarge': '64 vCPU, 512 GiB RAM',
  'db.r6i.24xlarge': '96 vCPU, 768 GiB RAM',
  // Valor herdado do legado. A AWS documenta 1.024 GiB para db.r6i.32xlarge; mantido como está
  // para preservar paridade com as propostas já emitidas. Revisar antes de corrigir.
  'db.r6i.32xlarge': '128 vCPU, 512 GiB RAM',

  // -------------------------------------------------------------------------
  // Famílias ausentes no legado. Sem elas o parser cai no nome cru do tipo, e a proposta exibe
  // `m7i.2xlarge` em vez da capacidade computacional — justamente o que a tabela existe para evitar.
  // -------------------------------------------------------------------------

  // --- EC2 uso geral, geração anterior ---
  't2.nano': '1 vCPU, 0.5 GiB RAM',
  't2.micro': '1 vCPU, 1 GiB RAM',
  't2.small': '1 vCPU, 2 GiB RAM',
  't2.medium': '2 vCPU, 4 GiB RAM',
  't2.large': '2 vCPU, 8 GiB RAM',
  't2.xlarge': '4 vCPU, 16 GiB RAM',
  't2.2xlarge': '8 vCPU, 32 GiB RAM',
  'm5a.large': '2 vCPU, 8 GiB RAM',
  'm5a.xlarge': '4 vCPU, 16 GiB RAM',
  'm5a.2xlarge': '8 vCPU, 32 GiB RAM',
  'm5a.4xlarge': '16 vCPU, 64 GiB RAM',
  'm5a.8xlarge': '32 vCPU, 128 GiB RAM',
  'm5a.12xlarge': '48 vCPU, 192 GiB RAM',
  'm5a.16xlarge': '64 vCPU, 256 GiB RAM',
  'm5a.24xlarge': '96 vCPU, 384 GiB RAM',

  // --- EC2 uso geral, Intel Sapphire Rapids ---
  'm7i.large': '2 vCPU, 8 GiB RAM',
  'm7i.xlarge': '4 vCPU, 16 GiB RAM',
  'm7i.2xlarge': '8 vCPU, 32 GiB RAM',
  'm7i.4xlarge': '16 vCPU, 64 GiB RAM',
  'm7i.8xlarge': '32 vCPU, 128 GiB RAM',
  'm7i.12xlarge': '48 vCPU, 192 GiB RAM',
  'm7i.16xlarge': '64 vCPU, 256 GiB RAM',
  'm7i.24xlarge': '96 vCPU, 384 GiB RAM',
  'm7i.48xlarge': '192 vCPU, 768 GiB RAM',
  'm7i-flex.large': '2 vCPU, 8 GiB RAM',
  'm7i-flex.xlarge': '4 vCPU, 16 GiB RAM',
  'm7i-flex.2xlarge': '8 vCPU, 32 GiB RAM',
  'm7i-flex.4xlarge': '16 vCPU, 64 GiB RAM',
  'm7i-flex.8xlarge': '32 vCPU, 128 GiB RAM',

  // --- EC2 uso geral, AMD Genoa ---
  'm7a.medium': '1 vCPU, 4 GiB RAM',
  'm7a.large': '2 vCPU, 8 GiB RAM',
  'm7a.xlarge': '4 vCPU, 16 GiB RAM',
  'm7a.2xlarge': '8 vCPU, 32 GiB RAM',
  'm7a.4xlarge': '16 vCPU, 64 GiB RAM',
  'm7a.8xlarge': '32 vCPU, 128 GiB RAM',
  'm7a.12xlarge': '48 vCPU, 192 GiB RAM',
  'm7a.16xlarge': '64 vCPU, 256 GiB RAM',
  'm7a.24xlarge': '96 vCPU, 384 GiB RAM',
  'm7a.32xlarge': '128 vCPU, 512 GiB RAM',
  'm7a.48xlarge': '192 vCPU, 768 GiB RAM',

  // --- EC2 uso geral, Graviton4 ---
  'm8g.medium': '1 vCPU, 4 GiB RAM',
  'm8g.large': '2 vCPU, 8 GiB RAM',
  'm8g.xlarge': '4 vCPU, 16 GiB RAM',
  'm8g.2xlarge': '8 vCPU, 32 GiB RAM',
  'm8g.4xlarge': '16 vCPU, 64 GiB RAM',
  'm8g.8xlarge': '32 vCPU, 128 GiB RAM',
  'm8g.12xlarge': '48 vCPU, 192 GiB RAM',
  'm8g.16xlarge': '64 vCPU, 256 GiB RAM',
  'm8g.24xlarge': '96 vCPU, 384 GiB RAM',
  'm8g.48xlarge': '192 vCPU, 768 GiB RAM',

  // --- EC2 computação, Intel Sapphire Rapids ---
  'c7i.large': '2 vCPU, 4 GiB RAM',
  'c7i.xlarge': '4 vCPU, 8 GiB RAM',
  'c7i.2xlarge': '8 vCPU, 16 GiB RAM',
  'c7i.4xlarge': '16 vCPU, 32 GiB RAM',
  'c7i.8xlarge': '32 vCPU, 64 GiB RAM',
  'c7i.12xlarge': '48 vCPU, 96 GiB RAM',
  'c7i.16xlarge': '64 vCPU, 128 GiB RAM',
  'c7i.24xlarge': '96 vCPU, 192 GiB RAM',
  'c7i.48xlarge': '192 vCPU, 384 GiB RAM',

  // --- EC2 computação, AMD Genoa ---
  'c7a.medium': '1 vCPU, 2 GiB RAM',
  'c7a.large': '2 vCPU, 4 GiB RAM',
  'c7a.xlarge': '4 vCPU, 8 GiB RAM',
  'c7a.2xlarge': '8 vCPU, 16 GiB RAM',
  'c7a.4xlarge': '16 vCPU, 32 GiB RAM',
  'c7a.8xlarge': '32 vCPU, 64 GiB RAM',
  'c7a.12xlarge': '48 vCPU, 96 GiB RAM',
  'c7a.16xlarge': '64 vCPU, 128 GiB RAM',
  'c7a.24xlarge': '96 vCPU, 192 GiB RAM',
  'c7a.32xlarge': '128 vCPU, 256 GiB RAM',
  'c7a.48xlarge': '192 vCPU, 384 GiB RAM',

  // --- EC2 computação, Graviton4 ---
  'c8g.medium': '1 vCPU, 2 GiB RAM',
  'c8g.large': '2 vCPU, 4 GiB RAM',
  'c8g.xlarge': '4 vCPU, 8 GiB RAM',
  'c8g.2xlarge': '8 vCPU, 16 GiB RAM',
  'c8g.4xlarge': '16 vCPU, 32 GiB RAM',
  'c8g.8xlarge': '32 vCPU, 64 GiB RAM',
  'c8g.12xlarge': '48 vCPU, 96 GiB RAM',
  'c8g.16xlarge': '64 vCPU, 128 GiB RAM',
  'c8g.24xlarge': '96 vCPU, 192 GiB RAM',
  'c8g.48xlarge': '192 vCPU, 384 GiB RAM',

  // --- EC2 memória, Intel Sapphire Rapids ---
  'r7i.large': '2 vCPU, 16 GiB RAM',
  'r7i.xlarge': '4 vCPU, 32 GiB RAM',
  'r7i.2xlarge': '8 vCPU, 64 GiB RAM',
  'r7i.4xlarge': '16 vCPU, 128 GiB RAM',
  'r7i.8xlarge': '32 vCPU, 256 GiB RAM',
  'r7i.12xlarge': '48 vCPU, 384 GiB RAM',
  'r7i.16xlarge': '64 vCPU, 512 GiB RAM',
  'r7i.24xlarge': '96 vCPU, 768 GiB RAM',
  'r7i.48xlarge': '192 vCPU, 1536 GiB RAM',

  // --- EC2 memória, AMD Genoa ---
  'r7a.medium': '1 vCPU, 8 GiB RAM',
  'r7a.large': '2 vCPU, 16 GiB RAM',
  'r7a.xlarge': '4 vCPU, 32 GiB RAM',
  'r7a.2xlarge': '8 vCPU, 64 GiB RAM',
  'r7a.4xlarge': '16 vCPU, 128 GiB RAM',
  'r7a.8xlarge': '32 vCPU, 256 GiB RAM',
  'r7a.12xlarge': '48 vCPU, 384 GiB RAM',
  'r7a.16xlarge': '64 vCPU, 512 GiB RAM',
  'r7a.24xlarge': '96 vCPU, 768 GiB RAM',
  'r7a.32xlarge': '128 vCPU, 1024 GiB RAM',
  'r7a.48xlarge': '192 vCPU, 1536 GiB RAM',

  // --- EC2 memória, Graviton4 ---
  'r8g.medium': '1 vCPU, 8 GiB RAM',
  'r8g.large': '2 vCPU, 16 GiB RAM',
  'r8g.xlarge': '4 vCPU, 32 GiB RAM',
  'r8g.2xlarge': '8 vCPU, 64 GiB RAM',
  'r8g.4xlarge': '16 vCPU, 128 GiB RAM',
  'r8g.8xlarge': '32 vCPU, 256 GiB RAM',
  'r8g.12xlarge': '48 vCPU, 384 GiB RAM',
  'r8g.16xlarge': '64 vCPU, 512 GiB RAM',
  'r8g.24xlarge': '96 vCPU, 768 GiB RAM',
  'r8g.48xlarge': '192 vCPU, 1536 GiB RAM',

  // --- EC2 memória extrema ---
  'x2idn.16xlarge': '64 vCPU, 1024 GiB RAM',
  'x2idn.24xlarge': '96 vCPU, 1536 GiB RAM',
  'x2idn.32xlarge': '128 vCPU, 2048 GiB RAM',
  'x2iedn.xlarge': '4 vCPU, 128 GiB RAM',
  'x2iedn.2xlarge': '8 vCPU, 256 GiB RAM',
  'x2iedn.4xlarge': '16 vCPU, 512 GiB RAM',
  'x2iedn.8xlarge': '32 vCPU, 1024 GiB RAM',
  'x2iedn.16xlarge': '64 vCPU, 2048 GiB RAM',
  'x2iedn.24xlarge': '96 vCPU, 3072 GiB RAM',
  'x2iedn.32xlarge': '128 vCPU, 4096 GiB RAM',

  // --- EC2 armazenamento NVMe ---
  'i3.large': '2 vCPU, 15.25 GiB RAM',
  'i3.xlarge': '4 vCPU, 30.5 GiB RAM',
  'i3.2xlarge': '8 vCPU, 61 GiB RAM',
  'i3.4xlarge': '16 vCPU, 122 GiB RAM',
  'i3.8xlarge': '32 vCPU, 244 GiB RAM',
  'i3.16xlarge': '64 vCPU, 488 GiB RAM',
  'i4i.large': '2 vCPU, 16 GiB RAM',
  'i4i.xlarge': '4 vCPU, 32 GiB RAM',
  'i4i.2xlarge': '8 vCPU, 64 GiB RAM',
  'i4i.4xlarge': '16 vCPU, 128 GiB RAM',
  'i4i.8xlarge': '32 vCPU, 256 GiB RAM',
  'i4i.12xlarge': '48 vCPU, 384 GiB RAM',
  'i4i.16xlarge': '64 vCPU, 512 GiB RAM',
  'i4i.32xlarge': '128 vCPU, 1024 GiB RAM',

  // --- EC2 acelerado por GPU ---
  'g4dn.xlarge': '4 vCPU, 16 GiB RAM',
  'g4dn.2xlarge': '8 vCPU, 32 GiB RAM',
  'g4dn.4xlarge': '16 vCPU, 64 GiB RAM',
  'g4dn.8xlarge': '32 vCPU, 128 GiB RAM',
  'g4dn.12xlarge': '48 vCPU, 192 GiB RAM',
  'g4dn.16xlarge': '64 vCPU, 256 GiB RAM',
  'g5.xlarge': '4 vCPU, 16 GiB RAM',
  'g5.2xlarge': '8 vCPU, 32 GiB RAM',
  'g5.4xlarge': '16 vCPU, 64 GiB RAM',
  'g5.8xlarge': '32 vCPU, 128 GiB RAM',
  'g5.12xlarge': '48 vCPU, 192 GiB RAM',
  'g5.16xlarge': '64 vCPU, 256 GiB RAM',
  'g5.24xlarge': '96 vCPU, 384 GiB RAM',
  'g5.48xlarge': '192 vCPU, 768 GiB RAM',

  // --- RDS, famílias ausentes no legado ---
  'db.t2.micro': '1 vCPU, 1 GiB RAM',
  'db.t2.small': '1 vCPU, 2 GiB RAM',
  'db.t2.medium': '2 vCPU, 4 GiB RAM',
  'db.t2.large': '2 vCPU, 8 GiB RAM',
  'db.m7g.large': '2 vCPU, 8 GiB RAM',
  'db.m7g.xlarge': '4 vCPU, 16 GiB RAM',
  'db.m7g.2xlarge': '8 vCPU, 32 GiB RAM',
  'db.m7g.4xlarge': '16 vCPU, 64 GiB RAM',
  'db.m7g.8xlarge': '32 vCPU, 128 GiB RAM',
  'db.m7g.12xlarge': '48 vCPU, 192 GiB RAM',
  'db.m7g.16xlarge': '64 vCPU, 256 GiB RAM',
  'db.m7i.large': '2 vCPU, 8 GiB RAM',
  'db.m7i.xlarge': '4 vCPU, 16 GiB RAM',
  'db.m7i.2xlarge': '8 vCPU, 32 GiB RAM',
  'db.m7i.4xlarge': '16 vCPU, 64 GiB RAM',
  'db.m7i.8xlarge': '32 vCPU, 128 GiB RAM',
  'db.m7i.12xlarge': '48 vCPU, 192 GiB RAM',
  'db.m7i.16xlarge': '64 vCPU, 256 GiB RAM',
  'db.m7i.24xlarge': '96 vCPU, 384 GiB RAM',
  'db.m7i.48xlarge': '192 vCPU, 768 GiB RAM',
  'db.r6a.large': '2 vCPU, 16 GiB RAM',
  'db.r6a.xlarge': '4 vCPU, 32 GiB RAM',
  'db.r6a.2xlarge': '8 vCPU, 64 GiB RAM',
  'db.r6a.4xlarge': '16 vCPU, 128 GiB RAM',
  'db.r6a.8xlarge': '32 vCPU, 256 GiB RAM',
  'db.r6a.12xlarge': '48 vCPU, 384 GiB RAM',
  'db.r6a.16xlarge': '64 vCPU, 512 GiB RAM',
  'db.r6a.24xlarge': '96 vCPU, 768 GiB RAM',
  'db.r6a.32xlarge': '128 vCPU, 1024 GiB RAM',
  'db.r7i.large': '2 vCPU, 16 GiB RAM',
  'db.r7i.xlarge': '4 vCPU, 32 GiB RAM',
  'db.r7i.2xlarge': '8 vCPU, 64 GiB RAM',
  'db.r7i.4xlarge': '16 vCPU, 128 GiB RAM',
  'db.r7i.8xlarge': '32 vCPU, 256 GiB RAM',
  'db.r7i.12xlarge': '48 vCPU, 384 GiB RAM',
  'db.r7i.16xlarge': '64 vCPU, 512 GiB RAM',
  'db.r7i.24xlarge': '96 vCPU, 768 GiB RAM',
  'db.r7i.48xlarge': '192 vCPU, 1536 GiB RAM',
  'db.x2g.large': '2 vCPU, 32 GiB RAM',
  'db.x2g.xlarge': '4 vCPU, 64 GiB RAM',
  'db.x2g.2xlarge': '8 vCPU, 128 GiB RAM',
  'db.x2g.4xlarge': '16 vCPU, 256 GiB RAM',
  'db.x2g.8xlarge': '32 vCPU, 512 GiB RAM',
  'db.x2g.12xlarge': '48 vCPU, 768 GiB RAM',
  'db.x2g.16xlarge': '64 vCPU, 1024 GiB RAM',
};

/**
 * Devolve a especificação do tipo de instância, ou `null` quando não catalogado.
 *
 * A busca é insensível a caixa: a calculadora já exportou `Instance Type (T3.Medium)` em algumas
 * versões, e devolver `null` nesse caso faria a proposta exibir o tipo cru sem necessidade.
 */
export function especificacaoDe(tipoInstancia: string): string | null {
  return ESPECIFICACOES_INSTANCIA[tipoInstancia.trim().toLowerCase()] ?? null;
}
